import { Link } from "@tanstack/react-router";
import { PersonCertificationsSection } from "@/components/persons/PersonCertificationsSection";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { useMyPerson } from "@/hooks/use-persons";

/**
 * Self-service view of just the certifications card. Lets a person see their
 * assigned certifications, statuses, expiry, and view any uploaded proof
 * without needing to find themselves in the People directory. Re-uses
 * PersonCertificationsSection in read-only mode (`isAdmin={false}`): only
 * admins may assign, change status, upload/replace proof, or remove — the
 * server enforces this (all cert mutations require an admin session).
 */
export function MyCertificationsPage() {
  const { data: person, isLoading, isError, error, refetch } = useMyPerson();

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load your record"}
        onRetry={() => refetch()}
      />
    );
  }
  if (!person) {
    return (
      <div className="space-y-6">
        <PageHeader title="My Certifications" />
        <div className="rounded-lg border bg-muted/30 p-6 text-sm">
          <p className="font-medium">No linked person record</p>
          <p className="mt-1 text-muted-foreground">
            Your account isn't linked to a person, so there are no certifications to show. An admin
            can link you from the People page.
          </p>
          <Link
            to="/dashboard/persons"
            className="mt-3 inline-block text-sm text-primary hover:underline"
          >
            Browse people
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="My Certifications"
        description="Your assigned certifications and their proof / expiry status."
      />
      <PersonCertificationsSection personId={person.id} personName={person.name} isAdmin={false} />
    </div>
  );
}
